import type { ReactNode } from 'react'
import type { CiSoftware, SoftwareMachine } from '../api/cmdb'

/**
 * Ce stie inventarul agentului despre un PROGRAM - fisa unui CI promovat din
 * ADM > Software (`refType` 3, `refId` = randul de instalare).
 *
 * De ce exista (devora, /app/cmdb/view/35 "sqlite-libs", 15 octombrie 2026):
 * fisa arata numai blocuri gandite pentru masini - cardul Asset gol si
 * „Observed by the relay - nothing has ever been reported". Un program n-are
 * serial, garantie sau trafic propriu; are nume, versiune, editor, data
 * instalarii si masina pe care sta.
 *
 * Toate valorile vin gata puse de server (`SoftwareCiCard`); aici doar se
 * deseneaza.
 */

const DASH = '—'

function size(bytes: number | null): string | null {
  if (bytes === null) return null
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`
  return `${bytes} B`
}

/** Masina, cu linkul spre fisa ei de inventar si spre CI-ul ei, cand il are. */
export function MachineLink({ machine }: { machine: SoftwareMachine }) {
  return (
    <>
      <a href={`/app/inventoree/device/${machine.id}`} target="_blank" rel="noopener">
        <i className="fas fa-desktop"></i> {machine.name}
      </a>
      {machine.ciId !== null && (
        <>
          {' '}
          <a
            href={`/app/cmdb/view/${machine.ciId}`}
            target="_blank"
            rel="noopener"
            className="adm-muted"
            style={{ fontSize: 12 }}
            title="The machine's configuration item"
          >
            (CI #{machine.ciId})
          </a>
        </>
      )}
    </>
  )
}

/** Randurile programului, in ordinea in care le cauta omul. */
export function programRows(software: CiSoftware): [string, ReactNode][] {
  const p = software.program
  const newer = p.latestVersion !== null && p.version !== null && p.latestVersion !== p.version
  return [
    ['Program', p.name],
    [
      'Version',
      p.version !== null ? (
        <>
          {p.version}
          {newer && (
            <span className="adm-muted" style={{ fontSize: 12 }}>
              {' '}
              &middot; newest seen in the fleet {p.latestVersion}
            </span>
          )}
        </>
      ) : null,
    ],
    ['Publisher', p.publisher],
    ['Category', p.category],
    [
      'Installed on',
      software.machine !== null ? (
        <>
          <MachineLink machine={software.machine} />
          {software.machine.removed && <span className="adm-muted"> (machine removed from inventory)</span>}
        </>
      ) : null,
    ],
    ['Install date', p.installed],
    ['First reported', p.firstSeen],
    [
      'Last reported',
      p.lastReported !== null || software.stillInstalled === false ? (
        <>
          {p.lastReported ?? ''}
          {software.stillInstalled === false && (
            <span className="adm-pill adm-pill-warn" style={{ marginLeft: 6 }}>
              no longer installed
            </span>
          )}
        </>
      ) : null,
    ],
    ['Size', size(p.sizeBytes)],
    ['Why it is in the CMDB', p.why],
  ]
}

/** Cardul programului - in locul cardului Asset, pe care un program nu-l are. */
export function SoftwareProgramCard({ software }: { software: CiSoftware }) {
  return (
    <div className="adm-card" id="cmdbSoftwareProgram">
      <div className="adm-card-head">
        <h5>
          <i className="fas fa-cube"></i> Installed program
        </h5>
        <span className="adm-card-note">from the agent's software inventory</span>
      </div>
      <table className="table adm-list">
        <tbody>
          {programRows(software).map(([label, value]) => (
            <tr key={label}>
              <td style={{ width: '40%' }}>{label}</td>
              <td>{value === null || value === '' ? <span className="adm-muted">{DASH}</span> : value}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="adm-muted small" style={{ padding: '0 12px 10px' }}>
        {software.found
          ? software.note
          : 'The installation this item was made from is no longer in the software inventory; this is what was known when it was added to the CMDB.'}
      </p>
    </div>
  )
}

/** Celelalte masini ale firmei cu acelasi program. */
export function ElsewhereTable({ software }: { software: CiSoftware }) {
  const e = software.elsewhere
  if (e.total === 0) {
    return (
      <div className="adm-empty-inline">
        <i className="fas fa-desktop"></i>{' '}
        {software.found
          ? 'No other machine of this customer reports this program.'
          : 'Not known: the installation this item was made from is gone.'}
      </div>
    )
  }
  return (
    <>
      <table className="table adm-list">
        <thead>
          <tr>
            <th>Machine</th>
            <th>Version</th>
          </tr>
        </thead>
        <tbody>
          {e.machines.map((m) => (
            <tr key={m.id}>
              <td>
                <MachineLink machine={m} />
              </td>
              <td className="adm-muted">{m.version ?? DASH}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {e.total > e.machines.length && (
        <p className="adm-muted small" style={{ padding: '0 12px 10px' }}>
          and {e.total - e.machines.length} more
        </p>
      )}
    </>
  )
}

/**
 * In locul lui „Observed by the relay": un program n-are trafic propriu pe
 * care sa-l raporteze releul. Ce se stie in plus e unde mai sta acelasi
 * program la acelasi client.
 */
export function SoftwareSection({ software }: { software: CiSoftware }) {
  return (
    <>
      <div className="adm-section">
        <h4>Same program at this customer</h4>
        <span className="adm-section-note" id="cmdbSoftwareNote">
          a program, not a machine - data only from the software inventory
          {software.program.lastReported !== null && <>, last reported {software.program.lastReported}</>}
        </span>
      </div>
      <div className="adm-card" id="cmdbSoftwareElsewhere">
        <div className="adm-card-head">
          <h5>
            <i className="fas fa-desktop"></i> Also installed on
          </h5>
          <span className="adm-card-note">
            {software.elsewhere.total} other machine{software.elsewhere.total === 1 ? '' : 's'}
          </span>
        </div>
        <ElsewhereTable software={software} />
      </div>
    </>
  )
}
